import React, { useState, useEffect } from 'react';
import { uploadService, dataService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import {
  FolderIcon,
  FileTextIcon,
  RefreshCwIcon,
  AlertTriangleIcon,
  SearchIcon,
  XCircleIcon,
  CheckIcon,
  SparklesIcon,
  DatabaseIcon,
  CodeIcon,
  PlusCircleIcon
} from 'lucide-react';

function DataEnhancement() {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [datasetColumns, setDatasetColumns] = useState([]);
  const [newColumnName, setNewColumnName] = useState('');
  const [newColumnDescription, setNewColumnDescription] = useState('');
  const [columnsToAdd, setColumnsToAdd] = useState([]);
  const [transformationType, setTransformationType] = useState('add_column'); // 'add_column', 'add_rows'
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [activeStep, setActiveStep] = useState('select'); // 'select', 'define', 'review'
  const { notify } = useNotification();
  const navigate = useNavigate();

  // Fetch data on component mount
  useEffect(() => {
    fetchUploads();
  }, []);

  // Fetch uploads from API with retry mechanism
  const fetchUploads = async (retries = 3, delay = 1000) => {
    let attempts = 0;
    
    const attemptFetch = async () => {
      try {
        setLoading(true);
        const data = await uploadService.getUploads();
        setUploads(data);
        return true;
      } catch (error) {
        console.error(`Error fetching uploads (attempt ${attempts+1}/${retries}):`, error);
        if (attempts >= retries - 1) {
          notify.error('Failed to load data sources');
          return false;
        }
        return false;
      } finally {
        setLoading(false);
      }
    };
    
    // First attempt
    attempts++;
    if (await attemptFetch()) return;
    
    // Retry with delay
    for (let i = 1; i < retries; i++) {
      attempts++;
      await new Promise(r => setTimeout(r, delay));
      if (await attemptFetch()) return;
    }
  };

  // Filter uploads based on search term
  const filteredUploads = searchTerm
    ? uploads.filter(upload => 
        upload.upload_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        upload.files.some(file => file.filename.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : uploads;

  // Format file size for display
  const formatFileSize = (sizeInBytes) => {
    if (sizeInBytes < 1024) {
      return `${sizeInBytes} B`;
    } else if (sizeInBytes < 1024 * 1024) {
      return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    } else if (sizeInBytes < 1024 * 1024 * 1024) {
      return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
    } else {
      return `${(sizeInBytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    }
  };

  // Format date to locale string
  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString();
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateStr || 'Unknown';
    }
  };

  // Handle dataset selection
  const handleSelectDataset = async (dataset) => {
    setSelectedDataset(dataset);
    setActiveStep('define');
    
    // Use default columns since we don't have a schema API yet
    setDatasetColumns([
      { name: 'product_id', type: 'string' },
      { name: 'name', type: 'string' },
      { name: 'description', type: 'string' },
      { name: 'price', type: 'number' },
      { name: 'category', type: 'string' },
      { name: 'in_stock', type: 'boolean' }
    ]);
    
    setColumnsToAdd([]);
  };

  // Add a new column to the list
  const handleAddColumn = () => {
    if (!newColumnName.trim()) {
      notify.warning('Please provide a column name');
      return;
    }

    if (!newColumnDescription.trim()) {
      notify.warning('Please provide a column description');
      return;
    }

    const newColumn = {
      id: Date.now().toString(),
      name: newColumnName.trim(),
      description: newColumnDescription.trim()
    };

    setColumnsToAdd([...columnsToAdd, newColumn]);
    setNewColumnName('');
    setNewColumnDescription('');
  };

  // Remove a column from the list
  const handleRemoveColumn = (id) => {
    setColumnsToAdd(columnsToAdd.filter(col => col.id !== id));
  };

  // Apply transformation without preview
  const handleApplyDirectly = async () => {
    if (transformationType === 'add_rows') {
      notify.info('This feature is coming soon');
      return;
    }

    if (columnsToAdd.length === 0) {
      notify.warning('Please add at least one column');
      return;
    }

    setIsProcessing(true);

    try {
      // Create columns object in the format required by the API
      const columnsObj = {};
      columnsToAdd.forEach(col => {
        // For now, we'll default all columns to string type
        columnsObj[col.name] = 'str';
      });
      
      // Create description from all column descriptions
      const description = columnsToAdd.map(col => 
        `${col.name}: ${col.description}`
      ).join('. ');
      
      // Get filename from the selected dataset
      let fileName = "";
      if (selectedDataset && selectedDataset.files && selectedDataset.files.length > 0) {
        fileName = selectedDataset.files[0].filename;
      }
      
      // Call the API to add columns
      const response = await dataService.addColumnsToDataset(
        selectedDataset.upload_id,
        fileName,
        columnsObj,
        description,
        true // save as new
      );
      
      // Handle the response
      // The API returns "job queued for processing" message when successful
      if (response && 
          (response.status === 'success' || 
           response.message?.toLowerCase().includes('queued') ||
           response.message?.toLowerCase().includes('job') ||
           response.message?.toLowerCase().includes('processing'))) {
        
        notify.success(
          response.message || 
          `${columnsToAdd.length > 1 ? columnsToAdd.length + ' columns' : 'Column'} generation job submitted successfully. This may take a few minutes to complete.`
        );
        
        // Reset state
        setNewColumnName('');
        setNewColumnDescription('');
        setColumnsToAdd([]);
        setActiveStep('select'); // Go back to dataset selection
        
        // Navigate to the new dataset if an ID is provided
        if (response.new_dataset_id) {
          navigate(`/data-sources/${response.new_dataset_id}`);
        } else {
          // If no dataset ID is provided, suggest checking data sources later
          notify.info('Your new dataset will appear in Data Sources when processing is complete');
        }
      } else {
        throw new Error('Operation failed: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error applying transformation:', error);
      notify.error(`Failed to add columns to dataset: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // This function is no longer used since we removed the preview step
  const handleApplyTransformation = async () => {
    // This is kept as a placeholder in case we need to restore preview functionality later
    console.warn('Preview functionality is disabled');
    setActiveStep('define');
  };

  // No longer needed as we removed preview functionality
  const handleCancelTransformation = () => {
    setActiveStep('define');
  };

  // Go back to dataset selection
  const handleBackToDatasets = () => {
    setSelectedDataset(null);
    setNewColumnName('');
    setNewColumnDescription('');
    setColumnsToAdd([]);
    setPreviewData(null);
    setActiveStep('select');
  };

  // Render empty state for data sources
  const renderEmptyDataSources = () => (
    <div className="text-center py-12">
      <FolderIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-slate-600" />
      <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No datasets found</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
        Upload a dataset first in the Data Sources section
      </p>
      <div className="mt-6">
        <button
          type="button"
          className="px-4 py-2 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 inline-flex items-center text-sm transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
          onClick={() => navigate('/data-sources')}
        >
          <DatabaseIcon className="w-4 h-4 mr-2" />
          Go to Data Sources
        </button>
      </div>
    </div>
  );

  // Render the list of datasets
  const renderDatasetSelection = () => (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="px-6 py-4 flex justify-between items-center border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Select a Dataset</h3>
          <button
            type="button"
            className="ml-3 inline-flex items-center px-2 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600 dark:hover:bg-slate-600 transition-all duration-200 transform hover:scale-[1.02] hover:shadow-md active:scale-[0.98]"
            onClick={() => fetchUploads()}
            disabled={loading}
          >
            <RefreshCwIcon className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        
        {/* Search input */}
        <div className="relative w-64">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SearchIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          </div>
          <input
            type="text"
            className="block w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-slate-700 dark:text-white dark:border-slate-600 dark:placeholder-gray-400"
            placeholder="Search datasets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <XCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-all duration-200 transform hover:scale-110" />
            </button>
          )}
        </div>
      </div>
      
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 mx-auto border-b-2 border-primary-600 dark:border-primary-500 rounded-full"></div>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading datasets...</p>
        </div>
      ) : filteredUploads.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
          {filteredUploads.map((upload) => (
            <div
              key={upload.upload_id}
              className="bg-white dark:bg-slate-800 overflow-hidden shadow-sm rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200 cursor-pointer transform hover:scale-[1.01] hover:-translate-y-0.5"
              onClick={() => handleSelectDataset(upload)}
            >
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center">
                    <FolderIcon className="h-6 w-6 text-primary-500 dark:text-primary-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {upload.files && upload.files.length > 0 && upload.files[0].filename
                          ? upload.files[0].filename.split('/').pop().replace(/\.[^/.]+$/, '')
                          : 'Dataset'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {upload.files.length} file{upload.files.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 space-y-2">
                  <div className="flex items-center">
                    <span>{formatDate(upload.created_at)}</span>
                  </div>
                  
                  <div className="mt-3 space-y-1">
                    {upload.files.slice(0, 3).map((file, index) => (
                      <div key={index} className="flex items-center">
                        <FileTextIcon className="h-3.5 w-3.5 mr-1.5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                        <div className="truncate">
                          <span className="font-medium text-xs text-gray-900 dark:text-gray-200 truncate">{file.filename}</span>
                          {file.size && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">({formatFileSize(file.size)})</span>
                          )}
                        </div>
                      </div>
                    ))}
                    {upload.files.length > 3 && (
                      <div className="text-xs text-gray-400 dark:text-gray-500 pl-5">
                        ...and {upload.files.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-slate-700/30 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  className="w-full inline-flex items-center justify-center px-2 py-1.5 text-xs font-medium rounded text-primary-700 dark:text-white bg-primary-50 dark:bg-primary-600 hover:bg-primary-100 dark:hover:bg-primary-700 transition-all duration-200 transform hover:scale-[1.02] hover:shadow-md active:scale-[0.98]"
                >
                  <SparklesIcon className="h-3.5 w-3.5 mr-1" />
                  Process Data
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-4">
          {renderEmptyDataSources()}
        </div>
      )}
    </div>
  );

  // Render the transformation form
  const renderTransformationForm = () => (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="px-6 py-4 flex justify-between items-center border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Define Data Transformation</h3>
          {selectedDataset && (
            <span className="ml-3 px-2 py-1 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded text-xs">
              Dataset: {selectedDataset.files && selectedDataset.files.length > 0 && selectedDataset.files[0].filename
                ? selectedDataset.files[0].filename.split('/').pop().replace(/\.[^/.]+$/, '')
                : selectedDataset.upload_id.substring(0, 8) + '...'}
            </span>
          )}
        </div>
        
        <button
          type="button"
          className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600 dark:hover:bg-slate-600 transition-all duration-200 transform hover:scale-[1.02] hover:shadow-md active:scale-[0.98]"
          onClick={handleBackToDatasets}
        >
          Back to Datasets
        </button>
      </div>
      
      <div className="p-6">
        {/* Schema display removed */}
        
        <form className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Transformation Type
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div 
                className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 transform hover:scale-[1.01] hover:shadow-md ${
                  transformationType === 'add_column' 
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-400'
                    : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                }`}
                onClick={() => setTransformationType('add_column')}
              >
                <div className="flex items-center mb-2">
                  <div className={`h-4 w-4 rounded-full mr-2 ${
                    transformationType === 'add_column' ? 'bg-primary-500 dark:bg-primary-400' : 'bg-gray-300 dark:bg-gray-600'
                  }`}></div>
                  <span className="font-medium text-gray-900 dark:text-white">Add Column</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Add a new column with custom values derived from existing data
                </p>
              </div>
              
              <div 
                className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 transform hover:scale-[1.01] hover:shadow-md ${
                  transformationType === 'add_rows' 
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-400'
                    : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                }`}
                onClick={() => setTransformationType('add_rows')}
              >
                <div className="flex items-center mb-2">
                  <div className={`h-4 w-4 rounded-full mr-2 ${
                    transformationType === 'add_rows' ? 'bg-primary-500 dark:bg-primary-400' : 'bg-gray-300 dark:bg-gray-600'
                  }`}></div>
                  <span className="font-medium text-gray-900 dark:text-white">Add Rows</span>
                  <span className="ml-2 px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                    Coming Soon
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Generate new rows based on existing data patterns
                </p>
              </div>
            </div>
          </div>
          
          {transformationType === 'add_column' ? (
            <>
              {/* Column adding form */}
              <div className="bg-gray-50 dark:bg-slate-700/20 rounded-lg p-5 mb-6">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Add New Column</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="column_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Column Name
                    </label>
                    <input
                      type="text"
                      id="column_name"
                      value={newColumnName}
                      onChange={(e) => setNewColumnName(e.target.value)}
                      placeholder="e.g., product_tier, sentiment_score"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                  </div>
                  <div>
                    <label htmlFor="column_description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Column Description
                    </label>
                    <textarea
                      id="column_description"
                      rows={1}
                      value={newColumnDescription}
                      onChange={(e) => setNewColumnDescription(e.target.value)}
                      placeholder="e.g., Product tier based on price ranges"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleAddColumn}
                  className="px-3 py-1.5 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 inline-flex items-center text-xs transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
                >
                  <PlusCircleIcon className="mr-1.5 h-3.5 w-3.5" />
                  Add Column
                </button>
              </div>
              
              {/* Columns list */}
              {columnsToAdd.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Columns to Create ({columnsToAdd.length})</h4>
                  <div className="space-y-2">
                    {columnsToAdd.map((col) => (
                      <div 
                        key={col.id} 
                        className="flex items-center justify-between bg-white dark:bg-slate-800 p-3 rounded-md border border-gray-200 dark:border-gray-700 shadow-sm"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {col.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                            {col.description}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRemoveColumn(col.id)}
                          className="ml-2 p-1 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition-all duration-200 transform hover:scale-110"
                        >
                          <XCircleIcon className="h-5 w-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {columnsToAdd.length === 0 && (
                <div className="text-center bg-gray-50 dark:bg-slate-700/20 rounded-lg p-6 mb-6">
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    No columns added yet. Add one or more columns above.
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="bg-gray-50 dark:bg-slate-700/30 rounded-lg p-6 text-center">
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                This feature is coming soon. Please check back later.
              </p>
            </div>
          )}
          
          {transformationType === 'add_column' && (
            <div className="flex justify-end">
              <button
                type="button"
                className={`px-4 py-2 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 inline-flex items-center text-sm transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] ${
                  isProcessing || columnsToAdd.length === 0 ? 'opacity-70 cursor-not-allowed' : ''
                }`}
                onClick={handleApplyDirectly}
                disabled={isProcessing || columnsToAdd.length === 0}
              >
                {isProcessing ? (
                  <>
                    <RefreshCwIcon className="animate-spin mr-2 h-5 w-5" />
                    Processing...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="mr-2 h-5 w-5" />
                    Add Columns
                  </>
                )}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
  
  // Preview functionality removed
  
  // Aggregation preview removed
  
  // Filter preview removed

  // Preview rendering removed

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Data Enricher</h1>
          <span className="ml-3 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
            Beta
          </span>
        </div>
      </div>
      
      <p className="text-gray-600 dark:text-gray-300 mb-8">
        Transform your datasets with LLM-driven enrichment. Add new columns based on existing data using natural language instructions.
      </p>
      
      {activeStep === 'select' && renderDatasetSelection()}
      {activeStep === 'define' && renderTransformationForm()}
      {/* Preview step removed */}
    </div>
  );
}

export default DataEnhancement;